import { useState } from 'react';
import { applyCostCategory, categoryPctRows, categoryRunRatePctRows, combineRows, money, pctOfMetric, scopeSummaryRow, scopedMetricValue, shortDeptLabel, variancePct } from './globalReporting';
import { GlobalCategoryBreakdownModal, GlobalDeepDiveModal, GlobalDeptHcNonHcModal, GlobalMonthSplitModal, GlobalPctDeepDiveModal, GlobalRecordsModal, GlobalRunRateModal, GlobalSourceByDeptModal } from './GlobalDrillModal';

// One consolidated row per department, summed across that department's own
// categories — the first thing this tab's tables show now, instead of every
// "{Department} — {Category}" row flattened out. combineRows already builds
// exactly this shape (monthly series + fy_*/run_rate fields) from any subset
// of category rows, so grouping by department and combining each group
// reuses it as-is; only the label/department/category fields need setting
// since combineRows defaults those to the page-wide "Total" row's shape.
// `label` is display-only (shortDeptLabel strips the trailing " Dashboard"
// every row/card/title on this page would otherwise repeat) — `department`
// stays the full backend label, since that's the value every filter/fetch/
// comparison elsewhere in this file matches against.
function departmentSummaryRows(categoryRows) {
    const groups = {};
    categoryRows.forEach(row => { (groups[row.department] ||= []).push(row); });
    // No explicit sort — groups' own insertion order already follows
    // categoryRows' order, which follows DEPARTMENTS in
    // global_views/dashboard.py, the one fixed sequence every department
    // list across the app now shows.
    // A department with nothing in Actual/AOP/PY for the selected scope
    // (e.g. a filter that excludes every record it ever had) is dropped —
    // just noise, same as every other category/department table.
    return Object.keys(groups).map(department => ({
        ...combineRows(groups[department]), label: shortDeptLabel(department), department, category: null,
    })).filter(row => row.fy_actual_forecast || row.fy_aop || row.fy_prior_year);
}

function VarianceStack({ actual, base }) {
    const diff = actual - base;
    const favorable = diff <= 0;
    return (
        <span style={{ color: favorable ? '#15803d' : '#dc2626' }}>
            <strong>{money(diff)}</strong><br /><small>{variancePct(actual, base)}</small>
        </span>
    );
}

// Live-computed equivalent of the 9 department dashboards' own "G&A
// Summary" tab, but rows are "{Department} — {Category}" instead of a
// single department's own categories, since Global Dashboard spans 9
// departments that each have their own unrelated category taxonomy (see
// global_views/summary.py's docstring). Sorted alphabetically, which groups
// each department's own rows together since every row starts with its
// department's label. Department/Category are page-level filters owned by
// GlobalMain.js (shared with the Dashboard and HC & Non-HC 360 tabs), not
// local state here.
function GlobalSummary({ data: rawData, quarter = [], customRange = null, period = [], yearMode = 'current', department = [], category = [], costCategory = [] }) {
    const data = applyCostCategory(rawData, costCategory);
    const currentYear = data.current_year;
    const priorYear = data.prior_year;
    const periodIsYtdOnly = period.includes('ytd') && !period.includes('fy');
    const periodLabel = periodIsYtdOnly ? 'YTD' : quarter.length || customRange ? 'Selected Period' : `FY${currentYear.slice(2)}`;
    const priorPeriodLabel = periodIsYtdOnly ? 'YTD' : quarter.length || customRange ? 'Selected Period' : `FY${priorYear.slice(2)}`;
    const isFullYear = !quarter.length && !customRange && !periodIsYtdOnly;

    const scopeRow = row => scopeSummaryRow(row, { quarter, customRange, period, asOf: data.as_of, yearMode, priorYear });
    const allCategoryRows = (data.categories || []).map(key => scopeRow(data.global_total.by_category[key]));

    const categoryRows = allCategoryRows.filter(r =>
        (!department.length || department.includes(r.department)) && (!category.length || category.includes(r.category)));
    const isFiltered = !!(department.length || category.length);
    // The page-level Category filter narrows every aggregate figure a drill
    // modal shows already, which makes it look like that category "carries
    // through" the whole drill-down — but GlobalRecordsModal fetches raw,
    // unfiltered records straight from /api/global/raw/ and relies entirely
    // on the drill-stack's OWN `category` field (not this page filter) to
    // narrow them client-side. A dive opened straight from a KPI card or a
    // "By Department" row never seeds that field (there's no single row to
    // have clicked yet), so once a user reaches records without ever
    // clicking an explicit category row inside a modal, that field is still
    // empty and every category's records show up. Falling back to the page
    // filter's own selection here — only when it's narrowed to exactly one
    // category, same as every other single-value fallback in this file —
    // closes that gap without touching any case where a category WAS
    // explicitly clicked (that always wins, see drillSplit/drillRecords
    // below).
    const singleCategoryFilter = category.length === 1 ? category[0] : null;
    const total = isFiltered ? combineRows(categoryRows) : scopeRow(data.global_total.total);
    const departmentRows = departmentSummaryRows(categoryRows);

    // Drill is a stack (same pattern as GlobalDashboard.js's own Total ->
    // department -> category drill-down) so a department row's popup can
    // itself be drilled further into one of its own categories instead of
    // being a dead end — clicking a department row opens straight at the
    // category-breakdown level (there's no "every department" level to show
    // first, since the table itself is already one row per department), and
    // clicking the Total row opens one level higher, at the full
    // department-breakdown view.
    const [drill, setDrill] = useState(null);
    const topDrill = drill && drill.length ? drill[drill.length - 1] : null;
    const openDrill = row => setDrill([{
        type: 'dive', title: shortDeptLabel(row.department), sub: periodLabel, months: row.monthly.map(m => m.month), department: row.department,
    }]);
    const openTotalDrill = row => setDrill([{
        type: 'dive', title: 'Total', sub: periodLabel, months: row.monthly.map(m => m.month), department: null,
    }]);
    const jumpDrill = index => setDrill(stack => stack.slice(0, index + 1));
    // Company-wide (no department filter) has no single department's
    // Headcount/Non-Headcount split, so every KPI tile / monthly cell /
    // Period Total in GlobalDeepDiveModal instead opens GlobalDeptHcNonHcModal
    // — same as GlobalDashboard.js's own Total KPI cards.
    const drillTotal = (totalMonths, label) => setDrill(stack => [...stack, {
        type: 'depthc', title: label, sub: 'Headcount vs. Non-Headcount split', months: totalMonths,
    }]);
    // Clicking Headcount or Non-Headcount in that split goes one level
    // deeper still: that one source's own total, broken out by department.
    const drillSource = (source, label) => setDrill(stack => [...stack, {
        type: 'depthc-source', title: `${topDrill.title} — ${label}`, sub: 'By department', source, months: topDrill.months,
    }]);
    // MonthSplitModal's own department-combined row pushes this — one level
    // deeper, that department's own category breakdown, generic over every
    // department (same as GlobalDashboard.js's own hcsplit drill).
    const drillCategories = () => setDrill(stack => [...stack, {
        type: 'dept-categories', title: `${topDrill.title} — ${shortDeptLabel(topDrill.department)}`, sub: 'By category',
        department: topDrill.department, months: topDrill.months, label: topDrill.label,
    }]);
    const drillCategoryRecords = categoryName => setDrill(stack => [...stack, {
        type: 'records', title: `${shortDeptLabel(topDrill.department)} — ${categoryName}`,
        sub: `Records — ${topDrill.label}`,
        department: topDrill.department, category: categoryName, months: topDrill.months, label: topDrill.label, source: 'total',
    }]);
    const drillDepartment = dept => setDrill(stack => [...stack, {
        type: 'dive', title: shortDeptLabel(dept), sub: `${topDrill.sub} — department breakdown`, months: topDrill.months, department: dept,
    }]);
    const drillCategory = cat => setDrill(stack => [...stack, {
        type: 'dive', title: `${shortDeptLabel(topDrill.department)} — ${cat}`, sub: `${topDrill.sub} — category detail`, months: topDrill.months, department: topDrill.department, category: cat,
    }]);
    const drillMonth = (month, label) => setDrill(stack => [...stack, {
        type: 'dive', title: `${topDrill.title} — ${label}`, sub: 'Single month', months: [month], department: topDrill.department, category: topDrill.category,
    }]);
    // Reached once a specific department is known (level 1 — every one of
    // its categories; or level 2 — one specific category), from a KPI tile/
    // Period Total (the whole scope) or a single month row — one level
    // deeper than the dollar drill used to go: a Headcount vs. Non-Headcount
    // split for that scope, then (one level deeper still) that source's own
    // records.
    const drillSplit = (splitMonths, label) => setDrill(stack => [...stack, {
        type: 'hcsplit', title: label, sub: 'Headcount vs. Non-Headcount split · click a row for its records',
        department: topDrill.department, category: topDrill.category || singleCategoryFilter, months: splitMonths, label,
    }]);
    const drillRecords = source => setDrill(stack => [...stack, {
        type: 'records', title: `${shortDeptLabel(topDrill.department)}${(topDrill.category || singleCategoryFilter) ? ` — ${topDrill.category || singleCategoryFilter}` : ''}`,
        sub: `${source === 'hc' ? 'Headcount' : 'Non-Headcount'} records — ${topDrill.label}`,
        department: topDrill.department, category: topDrill.category || singleCategoryFilter, months: topDrill.months, label: topDrill.label, source,
    }]);
    const breadcrumb = drill ? drill.map(d => d.title) : null;

    const revenueArr = data.revenue_arr || {};
    const metricOptions = { quarter, customRange, period, asOf: data.as_of, yearMode, priorYear };
    const revenueScopedActual = scopedMetricValue(revenueArr.revenue, 'actual', metricOptions);
    const arrScopedActual = scopedMetricValue(revenueArr.ending_arr, 'actual', metricOptions);
    const revenueScopedAop = scopedMetricValue(revenueArr.revenue, 'aop', metricOptions);
    const arrScopedAop = scopedMetricValue(revenueArr.ending_arr, 'aop', metricOptions);
    const revenueScopedPy = scopedMetricValue(revenueArr.revenue, 'py', metricOptions);
    const arrScopedPy = scopedMetricValue(revenueArr.ending_arr, 'py', metricOptions);
    const pctOfRow = (row, field = 'fy_actual_forecast') => {
        const [revenueDenom, arrDenom] = field === 'fy_aop' ? [revenueScopedAop, arrScopedAop]
            : field === 'fy_prior_year' ? [revenueScopedPy, arrScopedPy]
            : [revenueScopedActual, arrScopedActual];
        return {
            revenue: pctOfMetric(row[field], revenueDenom), arr: pctOfMetric(row[field], arrDenom),
            // Raw numerator/denominator behind each % above, for the
            // formula popup's "= $X ÷ $Y × 100 = value" line.
            _raw: { revenue: { num: row[field], denom: revenueDenom }, arr: { num: row[field], denom: arrDenom } },
        };
    };

    const fyMetricOptions = { quarter: '', customRange: null, period: 'fy', asOf: data.as_of, yearMode, priorYear };
    const revenueScopedFY = scopedMetricValue(revenueArr.revenue, 'actual', fyMetricOptions);
    const arrScopedFY = scopedMetricValue(revenueArr.ending_arr, 'actual', fyMetricOptions);
    const revenueAopFY = scopedMetricValue(revenueArr.revenue, 'aop', fyMetricOptions);
    const arrAopFY = scopedMetricValue(revenueArr.ending_arr, 'aop', fyMetricOptions);
    const pctOfRunRate = (row, field = 'current') => {
        if (field === 'variance') {
            const amount = row?.dec_fcst != null && row?.dec_aop != null ? row.dec_fcst - row.dec_aop : null;
            return {
                revenue: pctOfMetric(amount, revenueScopedFY), arr: pctOfMetric(amount, arrScopedFY),
                _raw: { revenue: { num: amount, denom: revenueScopedFY }, arr: { num: amount, denom: arrScopedFY } },
            };
        }
        const [revenueDenom, arrDenom] = field === 'dec_aop' ? [revenueAopFY, arrAopFY] : [revenueScopedFY, arrScopedFY];
        return {
            revenue: pctOfMetric(row?.[field], revenueDenom), arr: pctOfMetric(row?.[field], arrDenom),
            _raw: { revenue: { num: row?.[field], denom: revenueDenom }, arr: { num: row?.[field], denom: arrDenom } },
        };
    };

    // The 3 remaining popups this tab's cards/tables open, matching the
    // department dashboards' own Dashboard tab convention of every KPI card
    // and every table row being clickable, not just the first "By
    // Department" table: a percentage card-grid breakdown (same
    // GlobalPctDeepDiveModal the % KPI cards already use, just re-scoped to
    // one department's own categories when a row — rather than the page's
    // Total — was clicked), and a Run Rate department/category drill (same
    // GlobalRunRateModal + stack the Run Rate KPI cards already use).
    const pctVal = v => (v != null ? `${v.toFixed(1)}%` : 'N/A');
    const [pctDrill, setPctDrill] = useState(null);
    const pctCardsFor = (row, metric) => {
        const metricLabel = metric === 'revenue' ? 'Revenue' : 'ARR';
        const tone = metric === 'revenue' ? 'b' : 'c';
        const icon = metric === 'revenue' ? '💹' : '📡';
        const pAF = pctOfRow(row, 'fy_actual_forecast');
        const pAop = pctOfRow(row, 'fy_aop');
        const pPy = pctOfRow(row, 'fy_prior_year');
        const pVar = pctOfRow(row, 'fcst_vs_aop_full_year');
        return [
            { tone, icon, label: `${periodLabel} Actuals % of ${metricLabel}`, value: pctVal(pAF[metric]), formula: `Actuals+Forecast ÷ ${metricLabel} × 100`, numerator: pAF._raw[metric].num, denominator: pAF._raw[metric].denom },
            { tone, icon, label: `${periodLabel} AOP % of ${metricLabel}`, value: pctVal(pAop[metric]), formula: `AOP Budget ÷ AOP ${metricLabel} × 100`, numerator: pAop._raw[metric].num, denominator: pAop._raw[metric].denom },
            { tone, icon, label: `${priorPeriodLabel} PY % of ${metricLabel}`, value: pctVal(pPy[metric]), formula: `Prior Year Actuals ÷ Prior Year ${metricLabel} × 100`, numerator: pPy._raw[metric].num, denominator: pPy._raw[metric].denom },
            { tone, icon, label: `Var. to AOP % of ${metricLabel}`, value: pctVal(pVar[metric]), formula: `Var. to AOP ÷ ${metricLabel} × 100`, numerator: pVar._raw[metric].num, denominator: pVar._raw[metric].denom },
        ];
    };
    const openPctDrill = (row, metric) => {
        const deptCategories = row.department ? allCategoryRows.filter(r => r.department === row.department && (!category.length || category.includes(r.category))) : categoryRows;
        const [denomActual, denomAop, denomPy] = metric === 'revenue'
            ? [revenueScopedActual, revenueScopedAop, revenueScopedPy]
            : [arrScopedActual, arrScopedAop, arrScopedPy];
        setPctDrill({
            title: `${row.label} — % of ${metric === 'revenue' ? 'Revenue' : 'ARR'} Breakdown`, sub: periodLabel,
            cards: pctCardsFor(row, metric), categoryRows: categoryPctRows(deptCategories, denomActual, denomAop, denomPy),
        });
    };

    const runRateCategoryHeaders = ['Current RR', 'Dec AOP RR', 'Dec Fcst RR', 'Var to AOP RR'];
    const rrCardsFor = (row, metric) => {
        const metricLabel = metric === 'revenue' ? 'Revenue' : 'ARR';
        const tone = metric === 'revenue' ? 'b' : 'c';
        const icon = metric === 'revenue' ? '💹' : '📡';
        const pCur = pctOfRunRate(row.run_rate, 'current');
        const pAop = pctOfRunRate(row.run_rate, 'dec_aop');
        const pFcst = pctOfRunRate(row.run_rate, 'dec_fcst');
        const pVar = pctOfRunRate(row.run_rate, 'variance');
        return [
            { tone, icon, label: `Current Run Rate % of ${metricLabel}`, value: pctVal(pCur[metric]), formula: `Current Run Rate ÷ FY ${metricLabel} × 100`, numerator: pCur._raw[metric].num, denominator: pCur._raw[metric].denom },
            { tone, icon, label: `Dec AOP Run Rate % of ${metricLabel}`, value: pctVal(pAop[metric]), formula: `Dec AOP Run Rate ÷ FY AOP ${metricLabel} × 100`, numerator: pAop._raw[metric].num, denominator: pAop._raw[metric].denom },
            { tone, icon, label: `Dec Fcst Run Rate % of ${metricLabel}`, value: pctVal(pFcst[metric]), formula: `Dec Fcst Run Rate ÷ FY ${metricLabel} × 100`, numerator: pFcst._raw[metric].num, denominator: pFcst._raw[metric].denom },
            { tone, icon, label: `Var to AOP RR % of ${metricLabel}`, value: pctVal(pVar[metric]), formula: `(Dec Fcst Run Rate − Dec AOP Run Rate) ÷ FY ${metricLabel} × 100`, numerator: pVar._raw[metric].num, denominator: pVar._raw[metric].denom },
        ];
    };
    const openRunRatePctDrill = (row, metric) => {
        const deptCategories = row.department ? allCategoryRows.filter(r => r.department === row.department && (!category.length || category.includes(r.category))) : categoryRows;
        const [denomCurrent, denomDecAop] = metric === 'revenue' ? [revenueScopedFY, revenueAopFY] : [arrScopedFY, arrAopFY];
        setPctDrill({
            title: `${row.label} — Run Rate % of ${metric === 'revenue' ? 'Revenue' : 'ARR'} Breakdown`, sub: 'Annualized',
            cards: rrCardsFor(row, metric), categoryHeaders: runRateCategoryHeaders,
            categoryRows: categoryRunRatePctRows(deptCategories, denomCurrent, denomDecAop, denomCurrent),
        });
    };

    const [runRateDrill, setRunRateDrill] = useState(null);
    const topRunRateDrill = runRateDrill && runRateDrill.length ? runRateDrill[runRateDrill.length - 1] : null;
    const openRunRateRowDrill = row => setRunRateDrill([{ title: `Run Rate — ${shortDeptLabel(row.department)}`, sub: 'Company-wide · annualized — department breakdown', department: row.department }]);
    const openRunRateTotalDrill = () => setRunRateDrill([{ title: 'Run Rate — Total', sub: 'Company-wide · annualized', department: null }]);
    const jumpRunRateDrill = index => setRunRateDrill(stack => stack.slice(0, index + 1));
    const drillRunRateDepartment = dept => setRunRateDrill(stack => [...stack, {
        title: `Run Rate — ${shortDeptLabel(dept)}`, sub: 'Company-wide · annualized — department breakdown', department: dept,
    }]);

    return (
        <div className="esd">
            {topDrill?.type === 'dive' && (
                <GlobalDeepDiveModal
                    title={topDrill.title} sub={topDrill.sub} months={topDrill.months} department={topDrill.department} category={topDrill.category}
                    data={data} breadcrumb={breadcrumb} onJump={jumpDrill} onClose={() => setDrill(null)}
                    onDrillDepartment={drillDepartment} onDrillCategory={drillCategory} onDrillMonth={drillMonth} onDrillSplit={drillSplit} onDrillTotal={drillTotal}
                />
            )}
            {topDrill?.type === 'depthc' && (
                <GlobalDeptHcNonHcModal
                    title={topDrill.title} sub={topDrill.sub} months={topDrill.months}
                    data={data} breadcrumb={breadcrumb} onJump={jumpDrill} onClose={() => setDrill(null)}
                    onDrillSource={drillSource}
                />
            )}
            {topDrill?.type === 'depthc-source' && (
                <GlobalSourceByDeptModal
                    title={topDrill.title} sub={topDrill.sub} source={topDrill.source} months={topDrill.months}
                    data={data} breadcrumb={breadcrumb} onJump={jumpDrill} onClose={() => setDrill(null)}
                    onDrillDepartment={drillDepartment}
                />
            )}
            {topDrill?.type === 'hcsplit' && (
                <GlobalMonthSplitModal
                    title={topDrill.title} sub={topDrill.sub} department={topDrill.department} category={topDrill.category} months={topDrill.months}
                    data={data} breadcrumb={breadcrumb} onJump={jumpDrill} onClose={() => setDrill(null)}
                    onDrillSource={drillRecords} onDrillCategories={drillCategories}
                />
            )}
            {topDrill?.type === 'dept-categories' && (
                <GlobalCategoryBreakdownModal
                    title={topDrill.title} sub={topDrill.sub} department={topDrill.department} months={topDrill.months}
                    data={data} breadcrumb={breadcrumb} onJump={jumpDrill} onClose={() => setDrill(null)}
                    onDrillCategory={drillCategoryRecords}
                />
            )}
            {topDrill?.type === 'records' && (
                <GlobalRecordsModal
                    title={topDrill.title} sub={topDrill.sub} department={topDrill.department} source={topDrill.source}
                    category={topDrill.category} months={topDrill.months} currentYear={currentYear} priorYear={priorYear}
                    breadcrumb={breadcrumb} onJump={jumpDrill} onClose={() => setDrill(null)}
                />
            )}
            {topRunRateDrill && (
                <GlobalRunRateModal
                    label={topRunRateDrill.title} sub={topRunRateDrill.sub} department={topRunRateDrill.department}
                    data={data} breadcrumb={runRateDrill.map(d => d.title)} onJump={jumpRunRateDrill}
                    onClose={() => setRunRateDrill(null)} onDrillDepartment={drillRunRateDepartment}
                />
            )}
            {pctDrill && <GlobalPctDeepDiveModal {...pctDrill} onClose={() => setPctDrill(null)} />}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Company-Wide Summary{isFiltered ? ` — ${department.length ? department.map(shortDeptLabel).join(' + ') : 'All Departments'}${category.length ? ` — ${category.join(' + ')}` : ''}` : ''}{costCategory.length ? ` — ${costCategory.map(c => c === 'hc' ? 'Headcount' : c === 'commission' ? 'Commission' : 'Non-Headcount').join(' + ')}` : ''}</span>
            </div>

            <div className="esd-kpi-grid" style={{ marginBottom: 18 }}>
                <button type="button" className="esd-kpi-card esd-kpi-card--a" onClick={() => openTotalDrill({ ...total, label: 'Total', monthly: total.monthly, department: null })}>
                    <div className="esd-kpi-top"><span className="esd-kpi-hint">View detail →</span></div>
                    <div className="esd-kpi-label">{periodLabel} Actuals{isFullYear ? ' + Forecast' : ''}</div>
                    <div className="esd-kpi-value">{money(total.fy_actual_forecast)}</div>
                </button>
                <button type="button" className="esd-kpi-card esd-kpi-card--b" onClick={() => openTotalDrill({ ...total, label: 'Total', monthly: total.monthly, department: null })}>
                    <div className="esd-kpi-top"><span className="esd-kpi-hint">View detail →</span></div>
                    <div className="esd-kpi-label">{periodLabel} AOP</div>
                    <div className="esd-kpi-value">{money(total.fy_aop)}</div>
                </button>
                <button type="button" className="esd-kpi-card esd-kpi-card--c" onClick={() => openTotalDrill({ ...total, label: 'Total', monthly: total.monthly, department: null })}>
                    <div className="esd-kpi-top"><span className="esd-kpi-hint">View detail →</span></div>
                    <div className="esd-kpi-label">{priorPeriodLabel || 'Prior Year'}</div>
                    <div className="esd-kpi-value">{priorYear ? money(total.fy_prior_year) : 'N/A'}</div>
                </button>
                <button type="button" className={`esd-kpi-card esd-kpi-card--${total.fcst_vs_aop_full_year <= 0 ? 'good' : 'bad'}`} onClick={() => openTotalDrill({ ...total, label: 'Total', monthly: total.monthly, department: null })}>
                    <div className="esd-kpi-top"><span className="esd-kpi-hint">View detail →</span></div>
                    <div className="esd-kpi-label">{periodLabel} vs. AOP</div>
                    <div className={`esd-kpi-value tone-${total.fcst_vs_aop_full_year <= 0 ? 'good' : 'bad'}`}>{money(total.fcst_vs_aop_full_year)}</div>
                    <div className={`esd-kpi-sub tone-${total.fcst_vs_aop_full_year <= 0 ? 'good' : 'bad'}`}>{variancePct(total.fy_actual_forecast, total.fy_aop)}</div>
                </button>
                <button type="button" className={`esd-kpi-card esd-kpi-card--${total.fcst_vs_py_full_year <= 0 ? 'good' : 'bad'}`} onClick={() => openTotalDrill({ ...total, label: 'Total', monthly: total.monthly, department: null })}>
                    <div className="esd-kpi-top"><span className="esd-kpi-hint">View detail →</span></div>
                    <div className="esd-kpi-label">{periodLabel} vs. PY</div>
                    <div className={`esd-kpi-value tone-${total.fcst_vs_py_full_year <= 0 ? 'good' : 'bad'}`}>{money(total.fcst_vs_py_full_year)}</div>
                    <div className={`esd-kpi-sub tone-${total.fcst_vs_py_full_year <= 0 ? 'good' : 'bad'}`}>{variancePct(total.fy_actual_forecast, total.fy_prior_year)}</div>
                </button>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">🏷️ By Department</div>
                <div className="legal-ic-sub">Consolidated by department — click a row for its category breakdown, then drill further into any one category</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Department</th>
                                <th className="bm-th">{periodLabel} Actuals{isFullYear ? ' + Forecast' : ''}</th>
                                <th className="bm-th">{periodLabel} AOP</th>
                                <th className="bm-th">{priorYear ? `${priorPeriodLabel} PY` : 'PY (N/A)'}</th>
                                <th className="bm-th">Var. to AOP</th>
                                <th className="bm-th">Var. to PY</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departmentRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openDrill(row)}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{money(row.fy_actual_forecast)}</td>
                                    <td className="bm-td-r">{money(row.fy_aop)}</td>
                                    <td className="bm-td-r">{money(row.fy_prior_year)}</td>
                                    <td className="bm-td-r"><VarianceStack actual={row.fy_actual_forecast} base={row.fy_aop} /></td>
                                    <td className="bm-td-r"><VarianceStack actual={row.fy_actual_forecast} base={row.fy_prior_year} /></td>
                                </tr>
                            ))}
                            {!departmentRows.length && (
                                <tr><td colSpan={6} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="bm-row" style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openTotalDrill({ ...total, label: 'Total', monthly: total.monthly, department: null })}>
                                <td>Total</td>
                                <td className="bm-td-r">{money(total.fy_actual_forecast)}</td>
                                <td className="bm-td-r">{money(total.fy_aop)}</td>
                                <td className="bm-td-r">{money(total.fy_prior_year)}</td>
                                <td className="bm-td-r"><VarianceStack actual={total.fy_actual_forecast} base={total.fy_aop} /></td>
                                <td className="bm-td-r"><VarianceStack actual={total.fy_actual_forecast} base={total.fy_prior_year} /></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">💹 By Department — % of Revenue</div>
                <div className="legal-ic-sub">Each basis (Actuals, AOP, PY, Var. to AOP) as a share of its own matching-basis Revenue</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Department</th>
                                <th className="bm-th">{periodLabel} Actuals{isFullYear ? ' + Forecast' : ''} % of Revenue</th>
                                <th className="bm-th">{periodLabel} AOP % of Revenue</th>
                                <th className="bm-th">{priorYear ? `${priorPeriodLabel} PY` : 'PY (N/A)'} % of Revenue</th>
                                <th className="bm-th">Var. to AOP % of Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departmentRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openPctDrill(row, 'revenue')}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_actual_forecast').revenue != null ? `${pctOfRow(row, 'fy_actual_forecast').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_aop').revenue != null ? `${pctOfRow(row, 'fy_aop').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_prior_year').revenue != null ? `${pctOfRow(row, 'fy_prior_year').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fcst_vs_aop_full_year').revenue != null ? `${pctOfRow(row, 'fcst_vs_aop_full_year').revenue.toFixed(1)}%` : '—'}</td>
                                </tr>
                            ))}
                            {!departmentRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="bm-row" style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openPctDrill({ ...total, label: 'Total', department: null }, 'revenue')}>
                                <td>Total</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_actual_forecast').revenue != null ? `${pctOfRow(total, 'fy_actual_forecast').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_aop').revenue != null ? `${pctOfRow(total, 'fy_aop').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_prior_year').revenue != null ? `${pctOfRow(total, 'fy_prior_year').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fcst_vs_aop_full_year').revenue != null ? `${pctOfRow(total, 'fcst_vs_aop_full_year').revenue.toFixed(1)}%` : '—'}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">📡 By Department — % of ARR</div>
                <div className="legal-ic-sub">Each basis (Actuals, AOP, PY, Var. to AOP) as a share of its own matching-basis Ending ARR</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Department</th>
                                <th className="bm-th">{periodLabel} Actuals{isFullYear ? ' + Forecast' : ''} % of ARR</th>
                                <th className="bm-th">{periodLabel} AOP % of ARR</th>
                                <th className="bm-th">{priorYear ? `${priorPeriodLabel} PY` : 'PY (N/A)'} % of ARR</th>
                                <th className="bm-th">Var. to AOP % of ARR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departmentRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openPctDrill(row, 'arr')}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_actual_forecast').arr != null ? `${pctOfRow(row, 'fy_actual_forecast').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_aop').arr != null ? `${pctOfRow(row, 'fy_aop').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_prior_year').arr != null ? `${pctOfRow(row, 'fy_prior_year').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fcst_vs_aop_full_year').arr != null ? `${pctOfRow(row, 'fcst_vs_aop_full_year').arr.toFixed(1)}%` : '—'}</td>
                                </tr>
                            ))}
                            {!departmentRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="bm-row" style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openPctDrill({ ...total, label: 'Total', department: null }, 'arr')}>
                                <td>Total</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_actual_forecast').arr != null ? `${pctOfRow(total, 'fy_actual_forecast').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_aop').arr != null ? `${pctOfRow(total, 'fy_aop').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_prior_year').arr != null ? `${pctOfRow(total, 'fy_prior_year').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fcst_vs_aop_full_year').arr != null ? `${pctOfRow(total, 'fcst_vs_aop_full_year').arr.toFixed(1)}%` : '—'}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">🔁 Run Rate by Department</div>
                <div className="legal-ic-sub">Each row's own annualized Current / Dec AOP / Dec Fcst run rate</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Department</th>
                                <th className="bm-th">Current Run Rate</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} AOP Run Rate</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} FCST Run Rate</th>
                                <th className="bm-th">Var to AOP RR and %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departmentRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openRunRateRowDrill(row)}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{money(row.run_rate?.current)}</td>
                                    <td className="bm-td-r">{money(row.run_rate?.dec_aop)}</td>
                                    <td className="bm-td-r">{money(row.run_rate?.dec_fcst)}</td>
                                    <td className="bm-td-r"><VarianceStack actual={row.run_rate?.dec_fcst} base={row.run_rate?.dec_aop} /></td>
                                </tr>
                            ))}
                            {!departmentRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="bm-row" style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openRunRateTotalDrill()}>
                                <td>Total</td>
                                <td className="bm-td-r">{money(total.run_rate?.current)}</td>
                                <td className="bm-td-r">{money(total.run_rate?.dec_aop)}</td>
                                <td className="bm-td-r">{money(total.run_rate?.dec_fcst)}</td>
                                <td className="bm-td-r"><VarianceStack actual={total.run_rate?.dec_fcst} base={total.run_rate?.dec_aop} /></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">💹 Run Rate by Department — % of Revenue</div>
                <div className="legal-ic-sub">Each Run Rate basis as a share of its own matching-basis Revenue</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Department</th>
                                <th className="bm-th">Current Run Rate % of Revenue</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} AOP Run Rate % of Revenue</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} FCST Run Rate % of Revenue</th>
                                <th className="bm-th">Var to AOP RR % of Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departmentRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openRunRatePctDrill(row, 'revenue')}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'current').revenue != null ? `${pctOfRunRate(row.run_rate, 'current').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'dec_aop').revenue != null ? `${pctOfRunRate(row.run_rate, 'dec_aop').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'dec_fcst').revenue != null ? `${pctOfRunRate(row.run_rate, 'dec_fcst').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'variance').revenue != null ? `${pctOfRunRate(row.run_rate, 'variance').revenue.toFixed(1)}%` : '—'}</td>
                                </tr>
                            ))}
                            {!departmentRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="bm-row" style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openRunRatePctDrill({ ...total, label: 'Total', department: null }, 'revenue')}>
                                <td>Total</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'current').revenue != null ? `${pctOfRunRate(total.run_rate, 'current').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'dec_aop').revenue != null ? `${pctOfRunRate(total.run_rate, 'dec_aop').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'dec_fcst').revenue != null ? `${pctOfRunRate(total.run_rate, 'dec_fcst').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'variance').revenue != null ? `${pctOfRunRate(total.run_rate, 'variance').revenue.toFixed(1)}%` : '—'}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">📡 Run Rate by Department — % of ARR</div>
                <div className="legal-ic-sub">Each Run Rate basis as a share of its own matching-basis Ending ARR</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Department</th>
                                <th className="bm-th">Current Run Rate % of ARR</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} AOP Run Rate % of ARR</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} FCST Run Rate % of ARR</th>
                                <th className="bm-th">Var to AOP RR % of ARR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departmentRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openRunRatePctDrill(row, 'arr')}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'current').arr != null ? `${pctOfRunRate(row.run_rate, 'current').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'dec_aop').arr != null ? `${pctOfRunRate(row.run_rate, 'dec_aop').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'dec_fcst').arr != null ? `${pctOfRunRate(row.run_rate, 'dec_fcst').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'variance').arr != null ? `${pctOfRunRate(row.run_rate, 'variance').arr.toFixed(1)}%` : '—'}</td>
                                </tr>
                            ))}
                            {!departmentRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="bm-row" style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openRunRatePctDrill({ ...total, label: 'Total', department: null }, 'arr')}>
                                <td>Total</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'current').arr != null ? `${pctOfRunRate(total.run_rate, 'current').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'dec_aop').arr != null ? `${pctOfRunRate(total.run_rate, 'dec_aop').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'dec_fcst').arr != null ? `${pctOfRunRate(total.run_rate, 'dec_fcst').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'variance').arr != null ? `${pctOfRunRate(total.run_rate, 'variance').arr.toFixed(1)}%` : '—'}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
}

export { GlobalSummary };
export default GlobalSummary;
